import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { trackPromise } from 'react-promise-tracker';
import { alertActions,activityLogAction } from '_store';
import { fetchWrapper } from '_utils';
import msalInstance from 'authConfig';
import { ERR_MSG, SESSION_EXPIRED } from 'constants/labels';
import { authChannel } from '_utils/authChannel';
import { clearFiltersFromLocalStore } from '_utils/searchFilterLocalStorage';

// create slice

const name = 'auth';
const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();
const slice = createSlice({ name, initialState, reducers });

// exports

export const authActions = { ...slice.actions, ...extraActions };
export const authReducer = slice.reducer;

// implementation

function createInitialState() {
    return {
        value: null,
        userId: null,
        isAuthenticated: false,
        accessToken: null,
        isInitialized: false,
        marketers: [],
        activeMarketerId: null,
    }
}


function createReducers() {
    return {
        setAuth,
        clear,
        setIsInitialized,
        assignAccessToken,
        setActiveMarketer,
    };

    function setAuth(state, action) {
        state.value = action.payload;
        if (action.payload) {
            const user = action.payload.Data?.UserDetails;
            const userAccess = action.payload.Data?.UserAccess;

            state.userId = user?.id ?? null;
            state.isAuthenticated = true;

            if (user?.jwToken) {
              state.accessToken = user.jwToken;
            }

            // Portal 4 (Marketer & BBS) non-admin users (e.g. "Marketer CSP User",
            // "Marketer OPS User") may now belong to multiple Marketers - derive the
            // switchable Marketer list and default the active one to the first entry.
            const isAdmin = userAccess?.some(a => a.Role?.toLowerCase().includes('admin'));
            const portal4Access = userAccess?.find(a => a.PortalId === 4);
            const marketers = (!isAdmin && portal4Access?.Marketers) ? portal4Access.Marketers : [];
            state.marketers = marketers;
            state.activeMarketerId = marketers.length > 0 ? marketers[0].MarketerId : null;

            return;
        }
        state.userId = null;
        state.isAuthenticated = false;
        state.accessToken = null;
        state.marketers = [];
        state.activeMarketerId = null;
    }

    function setActiveMarketer(state, action) {
      const found = state.marketers?.find(m => m.MarketerId === action.payload);
      if (found) {
        state.activeMarketerId = found.MarketerId;
      }
    }

    function clear(state) {
      state.userId = null;
      state.isAuthenticated = false;
      state.value = null;
      state.accessToken = null;
      state.isInitialized = true;
      state.marketers = [];
      state.activeMarketerId = null;

      sessionStorage.removeItem('auth');
      sessionStorage.removeItem("userId");
      sessionStorage.removeItem("isAuthenticated");
      sessionStorage.removeItem('appMenuItems');
      sessionStorage.removeItem('portalID');
      sessionStorage.removeItem('isInternalUser');
      sessionStorage.removeItem('breadcrumb');
      sessionStorage.removeItem('mapcenterUserID');
      sessionStorage.removeItem('showHeaderMenu');

      localStorage.removeItem('appMenuItems');
      localStorage.removeItem('portalID');
      localStorage.removeItem('isInternalUser');
      localStorage.removeItem('breadcrumb');
      localStorage.removeItem('mapcenterUserID');
      localStorage.removeItem("showHeaderMenu");
      localStorage.removeItem("auth:lastActivity");
      clearFiltersFromLocalStore("mapCenterMapRequestList");
      clearFiltersFromLocalStore("userManagementList");
    }

    function setIsInitialized(state) {
      state.isInitialized = true;
    }

    function assignAccessToken(state, action) {
      if (action.payload) {
        state.accessToken = action.payload;
      }
    }
}

function createExtraActions() {
    const baseUrl = `${process.env.REACT_APP_API_URL}/api/Account`;

    return {
        login: login(),
        loginSSO: loginSSO(),
        logout: logout(),
        forgotPasswordRequest: forgotPasswordRequest(),
        resetPasswordRequest: resetPasswordRequest(),
        userPasswordVerification:userPasswordVerification(),
        resetPasswordByUser:resetPasswordByUser(),
        generateOtp: generateOtp(),
        validateOtp: validateOtp(),
        getUserDetailsAndAccess: getUserDetailsAndAccess(),
    };

    function getUserDetailsAndAccess() {
      return createAsyncThunk(
        `${name}/getUserDetailsAndAccess`,
        async (_, { dispatch }) => {
          dispatch(alertActions.clear());
          try {
            const user = await trackPromise(
              fetchWrapper.post(`${baseUrl}/GetUserDetailsAndAccess`),
            );
            if (user) {
              dispatch(authActions.setAuth(user));
              dispatch(authActions.setIsInitialized());
            } else {
              authActions.clear();
            }
          } catch { } 
        },
      );
    }

    function login() {
      return createAsyncThunk(
        `${name}/login`,
        async ({ Email, Password }, { dispatch, rejectWithValue }) => {
          dispatch(alertActions.clear());
          try {
            const user = await trackPromise(
              fetchWrapper.post(`${baseUrl}/Authenticate`, { Email, Password }),
            );
            return user;
          } catch (error) {
            return rejectWithValue(error);
          }
        },
      );
    }

    function loginSSO() {
      return createAsyncThunk(
        `${name}/loginSSO`,
        async ({ MicroEntraToken }, { dispatch, rejectWithValue }) => {
          dispatch(alertActions.clear());
          try {
            const user = await trackPromise(
              fetchWrapper.post(`${baseUrl}/AuthenticateByMicroEntraToken`, {
                MicroEntraToken,
              }),
            );
            return user;
          } catch (error) {
            return rejectWithValue(error);
          }
        },
      );
    }

    function logout() {
      return createAsyncThunk(
        `${name}/logout`,
        async (isTimeout = false, { getState, dispatch }) => {
          const state = getState();

          try {
            dispatch(alertActions.clear());
            const isInternalUser = JSON.parse(
              localStorage.getItem("isInternalUser"),
            );
            const response = await trackPromise(
              fetchWrapper.post(`${baseUrl}/Logout`),
            );

            if (response.Succeeded) {
              if (isInternalUser) {
                await msalInstance.initialize();
              }

              const user = state.auth.value?.Data?.UserDetails;
              if (!user) return;
              // Activity log implementation for logout
              await dispatch(activityLogAction.insert("A001")).unwrap();
              dispatch(authActions.clear());
            }
          } catch (error) {
            dispatch(
              alertActions.error({
                message: error.message ? ERR_MSG["073"] : error,
                header: "Logout Failed",
                showAfterRedirect: true,
              }),
            );
          } finally {
            if (isTimeout) {
              dispatch(
                alertActions.success({
                  message: SESSION_EXPIRED,
                  header: "Session Timeout",
                  showAfterRedirect: true,
                }),
              );
            } else {
              authChannel.postMessage({ type: "LOGOUT" });
            }
          }
        },
      );
    }

    function forgotPasswordRequest() {
        return createAsyncThunk(
            `${name}/forgotPasswordRequest`,
            async ({ email, requestFrom }, { rejectWithValue }) => {
                try {
                    const url = new URL(`${baseUrl}/forgot-password`);
                    url.searchParams.append('EmailAddress', email);
                    url.searchParams.append('requestFrom', requestFrom);
                    const response = await trackPromise(fetchWrapper.post(url.toString()));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function resetPasswordRequest() {
        return createAsyncThunk(
            `${name}/resetPasswordRequest`,
            async ({ token, newPassword }, { rejectWithValue }) => {
                try {
                    const response = await trackPromise(fetchWrapper.post(`${baseUrl}/reset-password`, { Token: token, Password: newPassword }));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function resetPasswordByUser() { 
        return createAsyncThunk(
            `${name}/resetPasswordbyUser`,
            async ({ userId, newPassword,currentPassword }, { rejectWithValue }) => {
                try {
                    const response = await trackPromise(fetchWrapper.post(`${baseUrl}/ResetPasswordbyUser`, { UserId: userId, NewPassword: newPassword,CurrentPassword:currentPassword }));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function userPasswordVerification() {  
        return createAsyncThunk(
            `${name}/userPasswordVerification`,
            async ({ userId, newPassword,currentPassword }, { rejectWithValue }) => {
                try {
                    const response = await trackPromise(fetchWrapper.post(`${baseUrl}/UserPasswordVerification`, { UserId: userId, NewPassword: newPassword,CurrentPassword:currentPassword }));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function generateOtp() {
        return createAsyncThunk(
            `${name}/generateOtp`,
            async ({ email }, { rejectWithValue }) => {
                try {
                    let obj = {
                        EmailAddress : email
                    };
                    const response = await trackPromise(fetchWrapper.post(`${baseUrl}/GenerateOtp`,obj));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }

    function validateOtp() {
        return createAsyncThunk(
            `${name}/validateOtp`,
            async ({ email, otp, newPassword }, { rejectWithValue }) => {
                try {
                    const response = await trackPromise(fetchWrapper.post(`${baseUrl}/ValidateOtp`, { EmailAddress: email, OTP: otp, NewPassword: newPassword }));
                    return response;
                } catch (error) {
                    return rejectWithValue(error);
                }
            }
        );
    }
}
